import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { PersonalDetails } from '@components/Profile/PersonalDetails';
import { StoreDetails } from '@components/Profile/StoreDetails';
import { useToast } from '@contexts/Toast';
import {
  OrderProcessingMasterData,
  ProfileDetailsCrumbSteps,
  responseTexts,
} from '@helpers/Constants';
import { Assets } from '@helpers/Constants/Assets';
import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { useLocalStorage } from '@hooks/useLocalStorage';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export const ProfileDetails: React.FC = () => {
  const { showToastMessage } = useToast();
  const { fetchResumeOnboardingStepId } = useLocalStorage();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);

  const [adminOnboardComplete, setAdminOnboardComplete] =
    React.useState<boolean>(false);

  const [roleNames, setRoleNames] = React.useState<string[]>([]);

  const BreadCrumbsProps = {
    steps: ProfileDetailsCrumbSteps,
    activeItem: 'Profile Details',
    type: OrderProcessingMasterData.PROFILE_DETAILS,
  };

  useEffect(() => {
    const isAdmin = localStorage.getItem('adminFlag') === '1';
    setIsAdmin(isAdmin);

    if (isAdmin) {
      checkOnboardingCompleteStatus();
    }

    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    if (userDetails.roleNames && Array.isArray(userDetails.roleNames)) {
      const roleNamesLower = userDetails.roleNames.map(
        (role: string) =>
          //make frst letter uppercase
          role.charAt(0).toUpperCase() + role.slice(1).toLowerCase(),
      );
      setRoleNames(roleNamesLower);
    }
  }, []);

  const checkOnboardingCompleteStatus = () => {
    const onboardingSteps = JSON.parse(
      localStorage.getItem('onboardingSteps') || '[]',
    );

    // Define the required steps with their routes and messages
    const requiredSteps = [
      {
        route: FrontendRoutes.LOGO,
        message: responseTexts.COMPLETE_LOGO_STEP,
      },
      {
        route: FrontendRoutes.ADDRESS,
        message: responseTexts.COMPLETE_ADDRESS_STEP,
      },
    ];

    // Check if first two steps are complete
    for (let i = 0; i < requiredSteps.length; i++) {
      if (!onboardingSteps?.[i]?.isComplete) {
        navigate(requiredSteps[i].route);
        showToastMessage(requiredSteps[i].message, 'error');
        return;
      }
    }

    // Check if all onboarding steps are complete
    if (
      onboardingSteps?.length > 0 &&
      onboardingSteps[onboardingSteps.length - 1]?.isComplete === 1
    ) {
      setAdminOnboardComplete(true);
    }
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      {(!isAdmin || adminOnboardComplete) && <SideNav active={10} />}
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader isOnBoarding={!adminOnboardComplete} />
        {!isAdmin && <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />}
        <div className="flex flex-col p-4 gap-4 overflow-y-auto">
          <div className="flex gap-2 items-center">
            {/* Back Navigation icon */}
            <div className="flex items-center justify-center">
              <button type="button" onClick={() => window.history.back()}>
                <svg
                  width="19"
                  height="14"
                  viewBox="0 0 19 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.5 7C5.5 6.71875 5.59375 6.46875 5.78125 6.28125L10.7812 1.28125C11.1562 0.875 11.8125 0.875 12.1875 1.28125C12.5938 1.65625 12.5938 2.3125 12.1875 2.6875L7.90625 7L12.1875 11.2812C12.5938 11.6562 12.5938 12.3125 12.1875 12.6875C11.8125 13.0938 11.1562 13.0938 10.7812 12.6875L5.78125 7.6875C5.59375 7.5 5.5 7.25 5.5 7Z"
                    fill="#1C1B1F"
                  />
                </svg>
              </button>
            </div>
            <h1 className="font-semibold text-xl text-[#1C1B1F]">
              Profile Details
            </h1>
          </div>
          {/* only for admin */}
          {isAdmin && !adminOnboardComplete && (
            <div className="flex flex-col bg-[#FFFBF3] border border-[#FFE9C3] rounded p-4 gap-4">
              <div className="flex gap-4">
                <div className="flex gap-4 ">
                  <img
                    src={Assets.STAR_ICON}
                    alt="Admin Icon"
                    className="w-10 h-10"
                  />
                  <p className="font-medium text-black text-lg">
                    Almost there! Please complete your settings to start using
                    Wild Game Butcher.
                  </p>
                </div>
              </div>
              <button
                type="submit"
                onClick={fetchResumeOnboardingStepId}
                className="w-full px-6 py-2 border border-transparent rounded-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
              >
                <span>Go to Settings Configuration</span>
              </button>
            </div>
          )}
          {/* Role Details */}
          {/* Admin */}
          {isAdmin && (
            <div className="flex flex-col gap-6 h-[74px]">
              <div className="bg-[#EFF6FF] border-l-4 border-[#1976D2] py-2 px-4 text-lg font-medium shadow-[0_1px_2px_0_#C5C9E2]">
                <h2 className="text-[#656565]">Role</h2>
                <p className="text-[#000A16]">Butcher Admin</p>
              </div>
            </div>
          )}
          {/* User */}
          {!isAdmin && (
            <div className="bg-[#EEF7EC] border-l-4 border-[#067456] py-2 px-4 text-lg font-medium">
              <h2 className="text-[#656565]">Role</h2>
              <p className="text-[#000A16]">{roleNames.join(', ')}</p>
            </div>
          )}
          {/* Personal Details */}
          {!isAdmin && <PersonalDetails />}
          {/* Store Details */}
          {isAdmin && <StoreDetails />}
        </div>
      </div>
    </div>
  );
};
